import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded';
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded';
import FormatAlignRightRoundedIcon from '@mui/icons-material/FormatAlignRightRounded';
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import FormatUnderlinedRoundedIcon from '@mui/icons-material/FormatUnderlinedRounded';
import StrikethroughSRoundedIcon from '@mui/icons-material/StrikethroughSRounded';
import CommonTypography from '@src/new-components/Typography';
import useFontCatalog from '@src/hooks/useFontCatalog';
import { FONT_STYLE } from '@src/constants/dubbing';
import {
  CAPTION_FONT_SIZE_RANGE,
  CAPTION_OUTLINE_WIDTH_RANGE,
  CAPTION_TEXT_ALIGN,
  CAPTION_TEXT_CASE,
  CAPTION_TEXT_FORMAT,
} from '@vbee-holding/aivoice-shared-lib';
import ConfigRow from './controls/ConfigRow';
import ChipTabs from './controls/ChipTabs';
import ChipToggles from './controls/ChipToggles';
import SelectField from './controls/SelectField';
import FontFamilySelect from './controls/FontFamilySelect';
import SliderField from './controls/SliderField';
import ColorField from './controls/ColorField';

// The control column is 200px wide by design. On rows that hold several
// fields, their widths plus the 8px gaps add back up to 200.
const CONTROL_WIDTH = 200;

const ALIGN_OPTIONS = [
  { value: CAPTION_TEXT_ALIGN.LEFT, icon: FormatAlignLeftRoundedIcon },
  { value: CAPTION_TEXT_ALIGN.CENTER, icon: FormatAlignCenterRoundedIcon },
  { value: CAPTION_TEXT_ALIGN.RIGHT, icon: FormatAlignRightRoundedIcon },
];

const FORMAT_OPTIONS = [
  { value: CAPTION_TEXT_FORMAT.BOLD, icon: FormatBoldRoundedIcon },
  { value: CAPTION_TEXT_FORMAT.ITALIC, icon: FormatItalicRoundedIcon },
  { value: CAPTION_TEXT_FORMAT.UNDERLINE, icon: FormatUnderlinedRoundedIcon },
  { value: CAPTION_TEXT_FORMAT.STRIKETHROUGH, icon: StrikethroughSRoundedIcon },
];

const TEXT_CASE_LABELS = {
  [CAPTION_TEXT_CASE.NORMAL]: 'captionTextCaseNormal',
  [CAPTION_TEXT_CASE.UPPERCASE]: 'captionTextCaseUppercase',
  [CAPTION_TEXT_CASE.LOWERCASE]: 'captionTextCaseLowercase',
  [CAPTION_TEXT_CASE.CAPITALIZE]: 'captionTextCaseCapitalize',
};

// The Bold chip and the font dropdown both drive weight, so one invariant
// holds: Bold on <=> weight >= 700. Anything lighter turns Bold off.
const { BOLD } = CAPTION_TEXT_FORMAT;
const BOLD_WEIGHT = 700;
const REGULAR_WEIGHT = 400;

// The face Bold switches to: the lightest one at 700 or above, because 700 is
// what bold means and anything heavier is a different look. Null when the family
// has no bold face — the chip then has nothing to switch to.
const boldWeightOf = (font) => {
  const weights = (font?.variants ?? [])
    .filter(
      ({ style, weight }) =>
        style === FONT_STYLE.NORMAL && weight >= BOLD_WEIGHT,
    )
    .map(({ weight }) => weight);
  return weights.length ? Math.min(...weights) : null;
};

// Add or drop the BOLD flag to match the weight, leaving other flags alone.
const syncBoldFormat = (formats, weight) => {
  const hasBold = formats.includes(BOLD);
  const shouldBold = weight >= BOLD_WEIGHT;
  if (hasBold === shouldBold) return formats;
  return shouldBold ? [...formats, BOLD] : formats.filter((f) => f !== BOLD);
};

const TextGroup = ({ text, outline, onChange, onOutlineChange }) => {
  const { t } = useTranslation();
  const fonts = useFontCatalog();

  const textCaseOptions = Object.entries(TEXT_CASE_LABELS).map(
    ([value, labelKey]) => ({ value, label: t(labelKey) }),
  );

  const boldWeight = useMemo(
    () => boldWeightOf(fonts.find(({ family }) => family === text.fontFamily)),
    [fonts, text.fontFamily],
  );

  // Only once the catalogue is in: an empty one means still loading, not a
  // family without a bold face.
  const formatOptions = useMemo(() => {
    if (!fonts.length || boldWeight) return FORMAT_OPTIONS;
    return FORMAT_OPTIONS.map((option) =>
      option.value === BOLD ? { ...option, disabled: true } : option,
    );
  }, [fonts, boldWeight]);

  // Weight to restore when the user turns Bold off again. Picking a weight
  // straight from the dropdown clears it, so the next unbold goes to Regular.
  const boldPrevWeightRef = useRef(null);

  const handleFontChange = ({ family, weight }) => {
    boldPrevWeightRef.current = null;
    onChange({
      fontFamily: family,
      fontWeight: weight,
      formats: syncBoldFormat(text.formats, weight),
    });
  };

  const handleFormatsChange = (nextFormats) => {
    const wasBold = text.formats.includes(BOLD);
    const nowBold = nextFormats.includes(BOLD);
    // Another flag changed (italic, underline...) — weight is not involved.
    if (wasBold === nowBold) {
      onChange({ formats: nextFormats });
      return;
    }
    // Bold switched on from a lighter weight: remember it, then jump to the
    // family's bold face — or to 700 while the catalogue is still loading.
    if (nowBold) {
      boldPrevWeightRef.current = text.fontWeight;
      onChange({
        formats: nextFormats,
        fontWeight: boldWeight ?? BOLD_WEIGHT,
      });
      return;
    }
    // Bold switched off: back to the remembered weight, or to Regular.
    const restored = boldPrevWeightRef.current ?? REGULAR_WEIGHT;
    boldPrevWeightRef.current = null;
    onChange({ formats: nextFormats, fontWeight: restored });
  };

  return (
    <div className="config-group">
      <CommonTypography className="config-group-label" variant="body-14-medium">
        {t('captionText')}
      </CommonTypography>
      <div className="config-group-rows">
        <ConfigRow label={t('captionTextAlign')}>
          <ChipTabs
            width={CONTROL_WIDTH}
            options={ALIGN_OPTIONS}
            value={text.align}
            onChange={(align) => onChange({ align })}
          />
        </ConfigRow>

        <ConfigRow label={t('captionFontFamily')}>
          <FontFamilySelect
            width={CONTROL_WIDTH}
            family={text.fontFamily}
            weight={text.fontWeight}
            onChange={handleFontChange}
          />
        </ConfigRow>

        <ConfigRow label={t('captionTextFormat')}>
          <ChipToggles
            width={CONTROL_WIDTH}
            options={formatOptions}
            values={text.formats}
            onChange={handleFormatsChange}
          />
        </ConfigRow>

        <ConfigRow label={t('captionTextCase')}>
          <SelectField
            width={CONTROL_WIDTH}
            options={textCaseOptions}
            value={text.textCase}
            onChange={(textCase) => onChange({ textCase })}
          />
        </ConfigRow>

        <ConfigRow label={t('captionFontSize')}>
          <SliderField
            width={CONTROL_WIDTH}
            value={text.fontSize}
            range={CAPTION_FONT_SIZE_RANGE}
            onChange={(fontSize) => onChange({ fontSize })}
          />
        </ConfigRow>

        <ConfigRow label={t('captionTextColor')}>
          <ColorField
            value={text.color}
            opacity={text.opacity}
            onChange={(color) => onChange({ color })}
            onOpacityChange={(opacity) => onChange({ opacity })}
          />
        </ConfigRow>

        <ConfigRow label={t('captionStrokeWidth')}>
          <SliderField
            width={CONTROL_WIDTH}
            value={outline.width}
            range={CAPTION_OUTLINE_WIDTH_RANGE}
            onChange={(width) => onOutlineChange({ width })}
          />
        </ConfigRow>

        <ConfigRow label={t('captionStrokeColor')}>
          <ColorField
            value={outline.color}
            opacity={outline.opacity}
            onChange={(color) => onOutlineChange({ color })}
            onOpacityChange={(opacity) => onOutlineChange({ opacity })}
          />
        </ConfigRow>
      </div>
    </div>
  );
};

export default TextGroup;
